/**
 * DEED-AI Main Application Component
 *
 * Handles routing and layout for the application.
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LoadingSpinner from './components/LoadingSpinner';
import Layout from './components/Layout';
import { ChatWidget } from './components/Chat';

// Lazy load views for code splitting
const LoginPage = lazy(() => import('./views/auth/LoginPage'));
const RegisterPage = lazy(() => import('./views/auth/RegisterPage'));
const AcceptInvitePage = lazy(() => import('./views/auth/AcceptInvitePage'));
const OwnerDashboard = lazy(() => import('./views/OwnerDashboard'));
const EmployeeDashboard = lazy(() => import('./views/EmployeeDashboard'));
const LeaveManagement = lazy(() => import('./views/LeaveManagement'));
const LeaveRequestForm = lazy(() => import('./views/LeaveRequestForm'));
const LeaveDetailView = lazy(() => import('./views/LeaveDetailView'));
const EmployeeManagement = lazy(() => import('./views/EmployeeManagement'));
const EmployeeForm = lazy(() => import('./views/EmployeeForm'));
const PayrollView = lazy(() => import('./views/PayrollView'));
const ReportsView = lazy(() => import('./views/ReportsView'));
const PoliciesView = lazy(() => import('./views/PoliciesView'));
const EmployeePoliciesView = lazy(() => import('./views/EmployeePoliciesView'));
const SettingsView = lazy(() => import('./views/SettingsView'));
const NotFoundPage = lazy(() => import('./views/NotFoundPage'));

/**
 * Protected route wrapper that requires authentication
 */
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading, userRole } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

/**
 * Public route that redirects authenticated users
 */
function PublicRoute({ children }) {
  const { user, loading, userRole } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (user) {
    // Redirect to appropriate dashboard based on role
    if (userRole === 'owner' || userRole === 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/employee" replace />;
  }

  return children;
}

/**
 * Main application routes
 */
function App() {
  const { user, userRole } = useAuth();

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/accept-invite/:token"
          element={<AcceptInvitePage />}
        />

        {/* Owner/Admin dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />

        {/* Employee dashboard */}
        <Route
          path="/employee"
          element={
            <ProtectedRoute>
              <EmployeeDashboard />
            </ProtectedRoute>
          }
        />

        {/* Leave management */}
        <Route
          path="/leave"
          element={
            <ProtectedRoute>
              <LeaveManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/request"
          element={
            <ProtectedRoute>
              <LeaveRequestForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/:id"
          element={
            <ProtectedRoute>
              <LeaveDetailView />
            </ProtectedRoute>
          }
        />

        {/* Employee management (owner/admin only) */}
        <Route
          path="/employees"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <EmployeeManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/new"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <EmployeeForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <EmployeeForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id/edit"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <EmployeeForm />
            </ProtectedRoute>
          }
        />

        {/* Payroll (owner/admin only) */}
        <Route
          path="/payroll"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <PayrollView />
            </ProtectedRoute>
          }
        />

        {/* Reports (owner/admin only) */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <ReportsView />
            </ProtectedRoute>
          }
        />

        {/* Policies - role-based view */}
        <Route
          path="/policies"
          element={
            <ProtectedRoute allowedRoles={['owner', 'admin']}>
              <PoliciesView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/policies"
          element={
            <ProtectedRoute>
              <EmployeePoliciesView />
            </ProtectedRoute>
          }
        />

        {/* Settings (owner only) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <SettingsView />
            </ProtectedRoute>
          }
        />

        {/* Root redirect */}
        <Route
          path="/"
          element={
            user ? (
              userRole === 'owner' || userRole === 'admin' ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/employee" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Chat Widget (only shown when authenticated) */}
      {user && <ChatWidget />}
    </Suspense>
  );
}

export default App;
